/**
 * ResetPassword — /auth/reset/:token
 *
 * Step 2 of the recovery flow. User landed here from the email link.
 * Confirms a new password (with re-enter match) and POSTs to
 * /api/auth/password/reset/confirm. On success, redirects to /login.
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import BloqLogo from "@/components/BloqLogo";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password/reset/confirm", {
        token,
        new_password: pw,
      });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bloq-container pt-7">
        <BloqLogo size={26} />
      </div>
      <div className="flex-1 grid place-items-center bloq-container py-12">
        <div className="max-w-md w-full" data-testid="reset-password-card">
          <p className="bloq-eyebrow mb-3">Account recovery</p>
          <h1 className="text-4xl bloq-headline mb-3">Choose a new password</h1>

          {done ? (
            <div className="bloq-card p-6 mt-6 flex flex-col items-center text-center gap-3" data-testid="reset-done-state">
              <div className="w-10 h-10 rounded-full bg-[#005e7a] grid place-items-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <p className="font-semibold">Password updated</p>
              <p className="text-sm text-[#4a4a52]">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <p className="text-[#4a4a52] text-base mb-8">
                Make it at least 6 characters. Re-enter to confirm.
              </p>
              <form onSubmit={onSubmit} className="flex flex-col gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                    New password
                  </label>
                  <input
                    type="password"
                    required
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="bloq-input mt-1"
                    data-testid="reset-pw-input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                    Re-enter password
                  </label>
                  <input
                    type="password"
                    required
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className={`bloq-input mt-1 ${
                      pw2 && pw !== pw2 ? "border-[#c43838]" : ""
                    }`}
                    data-testid="reset-pw2-input"
                  />
                  {pw2 && pw !== pw2 && (
                    <p className="text-xs text-[#c43838] mt-1" data-testid="reset-mismatch-warning">
                      Passwords don&apos;t match yet
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={busy || pw.length < 6 || pw !== pw2}
                  className={`mt-3 px-6 py-3 ${
                    busy || pw.length < 6 || pw !== pw2
                      ? "bloq-btn-secondary opacity-50 cursor-not-allowed"
                      : "bloq-btn-primary"
                  }`}
                  data-testid="reset-submit-button"
                >
                  {busy ? "Updating…" : "Update password"}
                </button>
              </form>
              <Link
                to="/login"
                className="mt-7 inline-flex items-center gap-1.5 text-sm text-[#4a4a52] hover:text-[#1a1a1f]"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Cancel
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
